import React from "react";
import { useAssessment, PHASES } from "../context/AssessmentContext";
import { DOMAINS } from "../data/domains";

const STATS = [
  { value: "20–23", label: "Adaptive Questions" },
  { value: "6",     label: "Operational Domains" },
  { value: "100pt", label: "Weighted Score" },
  { value: "4",     label: "Readiness Tiers" },
];

const FEATURES = [
  { icon: "🧠", title: "Adaptive Intelligence",   desc: "Questions deepen based on your maturity level — not a static quiz." },
  { icon: "📊", title: "Weighted Scoring",         desc: "Absentee ballot & legal questions carry premium weight due to risk exposure." },
  { icon: "🧪", title: '"What If" Simulator',     desc: "After results, simulate improvements and see your potential score live." },
  { icon: "🎯", title: "Compliance Wedge",        desc: "Exposes both operational inefficiency and legal/public risk simultaneously." },
];

export default function LandingPage() {
  const { setPhase } = useAssessment();

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>


      {/* ── Hero ── */}
      <div style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: "80px 24px 60px",
        textAlign: "center",
        position: "relative",
      }}>
        {/* Badge */}
        <div className="fade-up" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "5px 16px",
          background: "rgba(59,130,246,0.09)",
          border: "1px solid rgba(59,130,246,0.22)",
          borderRadius: 99,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--accent-blue)",
          letterSpacing: "0.08em",
          marginBottom: 28,
        }}>
          ✦ INTELLIGENT DIAGNOSTIC & LEAD GENERATION PLATFORM
        </div>

        {/* Headline */}
        <h1 className="fade-up-1 display" style={{
          fontSize: "clamp(32px, 5vw, 64px)",
          fontWeight: 700,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          color: "var(--text-primary)",
          marginBottom: 22,
        }}>
          Election Readiness <span style={{ color: "var(--accent-blue)" }}>Scorer™</span>
        </h1>

        {/* Sub */}
        <p className="fade-up-2" style={{
          fontSize: "clamp(15px, 2vw, 19px)",
          color: "var(--text-muted)",
          maxWidth: 580,
          margin: "0 auto 48px",
          lineHeight: 1.7,
        }}>
          Discover your jurisdiction's election readiness in{" "}
          <strong style={{ color: "var(--text-secondary)" }}>5–7 minutes</strong>.
          A personalised diagnostic across 6 operational domains with adaptive
          questioning that surfaces your exact pain points.
        </p>

        {/* CTA */}
        <div className="fade-up-3" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => setPhase(PHASES.ASSESSMENT)}
            className="btn btn-primary"
            style={{ fontSize: 17, padding: "16px 44px", letterSpacing: "-0.01em" }}
          >
            🚀 Start Your Assessment
          </button>
          <button className="btn btn-ghost" style={{ fontSize: 15, padding: "16px 28px" }}>
            Watch Demo ▶
          </button>
        </div>

        <p className="fade-up-4" style={{
          marginTop: 16, fontSize: 12, color: "var(--text-muted)",
        }}>
          Free · No credit card · Takes 5–7 minutes
        </p>
      </div>

      {/* ── Stats row ── */}
      <div className="fade-up-3" style={{
        maxWidth: 720,
        margin: "0 auto 64px",
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 1,
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden",
      }}>
        {STATS.map((s, i) => (
          <div key={s.label} style={{
            padding: "22px 16px",
            background: "var(--bg-card)",
            textAlign: "center",
            borderRight: i < STATS.length - 1 ? "1px solid var(--border)" : "none",
          }}>
            <div style={{
              fontFamily: "var(--font-mono)",
              fontSize: 26,
              fontWeight: 700,
              color: "var(--accent-blue)",
              lineHeight: 1,
              marginBottom: 6,
            }}>
              {s.value}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── Domain cards ── */}
      <div style={{ maxWidth: 960, margin: "0 auto 64px", padding: "0 24px" }}>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--text-muted)",
          letterSpacing: "0.1em",
          textAlign: "center",
          marginBottom: 20,
        }}>
          6 OPERATIONAL DOMAINS ASSESSED
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
        }}>
          {DOMAINS.map((d, i) => (
            <div
              key={d.id}
              className="fade-up card"
              style={{
                animationDelay: `${i * 0.06}s`,
                padding: "18px 20px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{
                position: "absolute",
                top: 0, right: 0,
                width: 80, height: 80,
                background: `radial-gradient(circle at top right, rgba(${d.colorRgb},0.1) 0%, transparent 70%)`,
              }} />
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 22 }}>{d.icon}</span>
                {d.premium && (
                  <span className="badge badge-premium">PREMIUM</span>
                )}
              </div>
              <div style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: 14,
                color: "var(--text-primary)",
                marginBottom: 5,
                lineHeight: 1.3,
              }}>
                {d.name}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
                {d.description}
              </div>
              <div style={{
                marginTop: 12,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: d.color,
              }}>
                {d.coreQCount} core · {d.adaptiveQRange} adaptive
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Features ── */}
      <div style={{ maxWidth: 860, margin: "0 auto 80px", padding: "0 24px" }}>
        <div style={{
          textAlign: "center",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--text-muted)",
          letterSpacing: "0.1em",
          marginBottom: 28,
        }}>
          KEY INNOVATION: HYBRID ADAPTIVE ARCHITECTURE
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {FEATURES.map((f) => (
            <div key={f.title} className="card" style={{ padding: "22px 24px" }}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>{f.icon}</div>
              <div style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: 15,
                marginBottom: 6,
                color: "var(--text-primary)",
              }}>
                {f.title}
              </div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
                {f.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom CTA ── */}
      <div style={{
        maxWidth: 600,
        margin: "0 auto 80px",
        padding: "44px 32px",
        background: "var(--bg-card-hover)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-xl)",
        textAlign: "center",
      }}>
        <div style={{
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: 26,
          color: "var(--text-primary)",
          marginBottom: 10,
          letterSpacing: "-0.02em",
        }}>
          Ready to discover your gaps?
        </div>
        <p style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 24 }}>
          <em>"This is not a quiz. This is a diagnostic tool disguised as an engaging experience."</em>
        </p>
        <button
          onClick={() => setPhase(PHASES.ASSESSMENT)}
          className="btn btn-primary"
          style={{ fontSize: 16, padding: "14px 40px" }}
        >
          Begin Assessment →
        </button>
      </div>
    </div>
  );
}
